import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import * as z from 'zod';

import { useShowBillingPage } from '#components/feature-flag/use-show-billing-flow.js';
import { Tabs } from '#components/tabs/tabs.js';
import { useTRPC } from '#src/trpc.js';
import { RouteAuthorizationError } from '#utils/route-authorization.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { OrgSettingsBillingTab } from './billing/org-settings-billing-tab.js';
import { OrgSettingsTab } from './org-settings-tab.js';
import { OrgSettingsUsersTab } from './org-settings-users-tab.js';

import type { TabDef } from '#components/tabs/tabs.js';

export const Route = createFileRoute('/_authorization-gate/orgs/$orgName/settings/')({
	validateSearch: z.object({
		/** Which settings tab to open; lets other flows deep-link the billing tab. */
		tab: z.enum(['users', 'billing']).catch('users').default('users'),
	}),
	staticData: {
		authorize: (ability) => ability.can('manage', 'organization'),
		navigation: {
			label: 'Org Settings',
			keywords: ['organization', 'users', 'settings'],
		},
	},
	beforeLoad: async ({ context, params }) => {
		const currentUser = await context.queryClient.ensureQueryData(context.trpc.user.getCurrent.queryOptions());
		const isOrgMember = currentUser.memberships.some((m) => m.groupUsername === params.orgName);
		if (!currentUser.auth.isStaff && !isOrgMember) {
			throw new RouteAuthorizationError();
		}
	},
	head: () => ({ meta: [{ title: 'Settings • Cadstrom' }] }),
	component: OrgSettingsPage,
});

const allTabDefs = [
	{ id: 'users', label: 'Users' },
	{ id: 'billing', label: 'Billing' },
] as const satisfies TabDef[];

type OrgSettingsTabId = (typeof allTabDefs)[number]['id'];

function OrgSettingsPage() {
	const { orgName } = Route.useParams();
	const { tab } = Route.useSearch();
	const navigate = Route.useNavigate();
	const trpc = useTRPC();

	const showBilling = useShowBillingPage(orgName);
	const tabDefs = showBilling ? allTabDefs : allTabDefs.filter((t) => t.id !== 'billing');
	// Derived from the URL (not local state) so a `?tab=billing` deep link still
	// lands on billing once the async billing gate resolves to shown.
	const selectedTab: OrgSettingsTabId = tab === 'billing' && showBilling ? 'billing' : 'users';
	const setSelectedTab = (id: OrgSettingsTabId) => {
		void navigate({ search: { tab: id }, replace: true });
	};

	const { data } = useSuspenseQuery(trpc.organization.get.queryOptions({ organizationName: orgName }));
	const org = data.organization;

	const el = useDataElTagGenerator(OrgSettingsPage);
	return (
		<div {...el()} className='flex min-h-0 grow flex-col gap-3'>
			<div className='mx-3'>
				<Tabs
					tabs={tabDefs}
					selectedTab={selectedTab}
					onSelectedTabChange={setSelectedTab}
					renderTab={OrgSettingsTab}
				/>
			</div>

			{selectedTab === 'users' && <OrgSettingsUsersTab organizationId={org.id} />}
			{selectedTab === 'billing' && showBilling && <OrgSettingsBillingTab organizationId={org.id} orgName={orgName} />}
		</div>
	);
}
